import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Phone } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface SMSSettings {
  id: string;
  is_enabled: boolean;
  twilio_account_sid: string | null;
  twilio_auth_token: string | null;
  twilio_phone_number: string | null;
  created_at?: string;
  updated_at?: string;
}

export const SMSProviderSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SMSSettings | null>(null);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sms_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!data) {
        // Create default settings
        const { data: newSettings, error: insertError } = await supabase
          .from('sms_settings')
          .insert({
            is_enabled: true,
            twilio_account_sid: null,
            twilio_auth_token: null,
            twilio_phone_number: null
          })
          .select()
          .single();

        if (insertError) throw insertError;
        setSettings(newSettings);
      } else {
        setSettings(data);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error loading SMS settings",
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('sms_settings')
        .update({
          is_enabled: settings.is_enabled,
          twilio_account_sid: settings.twilio_account_sid,
          twilio_auth_token: settings.twilio_auth_token,
          twilio_phone_number: settings.twilio_phone_number,
          updated_at: new Date().toISOString()
        })
        .eq('id', settings.id);

      if (error) throw error;

      toast({
        title: "Settings saved",
        description: "SMS provider settings have been updated successfully."
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error saving settings",
        description: error.message
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">No SMS settings found.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
          <CollapsibleTrigger className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Phone className="h-5 w-5 text-primary" />
              </div>
              <div className="text-left">
                <CardTitle>SMS Provider Settings (Twilio)</CardTitle>
                <CardDescription>Configure your Twilio account for sending SMS messages</CardDescription>
              </div>
            </div>
            <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="sms-enabled">Enable SMS Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Allow the system to send SMS messages via Twilio
                </p>
              </div>
              <Switch
                id="sms-enabled"
                checked={settings.is_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
              />
            </div>

            {settings.is_enabled && (
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="twilio_account_sid">
                    Twilio Account SID
                  </Label>
                  <Input
                    id="twilio_account_sid"
                    type="text"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={settings.twilio_account_sid || ''}
                    onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Find this in your Twilio console dashboard
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twilio_auth_token">
                    Twilio Auth Token
                  </Label>
                  <Input
                    id="twilio_auth_token"
                    type="password"
                    placeholder="••••••••••••••••••••••••••••••••"
                    value={settings.twilio_auth_token || ''}
                    onChange={(e) => setSettings({ ...settings, twilio_auth_token: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Your Twilio authentication token
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twilio_phone_number">
                    Twilio Phone Number
                  </Label>
                  <Input
                    id="twilio_phone_number"
                    type="tel"
                    placeholder="+1234567890"
                    value={settings.twilio_phone_number || ''}
                    onChange={(e) => setSettings({ ...settings, twilio_phone_number: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Your Twilio phone number in E.164 format (e.g., +1234567890)
                  </p>
                </div>
              </div>
            )}

            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
